import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  createOrUpdateSlot,
  getSlotById,
  SlotCreateUpdatePayload,
} from "../../api/slots";
import { getClinicsByUser } from "../../api/clinic";

const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

// Reuse same style as ClinicForm: 30-minute time options in 12-hour format
const TIME_OPTIONS: string[] = Array.from({ length: 48 }, (_, index) => {
  const hours24 = Math.floor(index / 2);
  const minutes = index % 2 === 0 ? 0 : 30;
  const period = hours24 < 12 ? "AM" : "PM";
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;
  const hourStr = hours12.toString().padStart(2, "0");
  const minuteStr = minutes === 0 ? "00" : "30";
  return `${hourStr}:${minuteStr} ${period}`;
});

const getDayFromDate = (dateStr: string) => {
  if (!dateStr) return "";
  const parsed = dayjs(dateStr);
  return parsed.isValid() ? parsed.format("ddd") : "";
};

const SlotsForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);
  const fetchedClinicsRef = useRef(false);
  const [clinicsLoading, setClinicsLoading] = useState(false);
  const [clinics, setClinics] = useState<{ id: string; name: string }[]>([]);
  const [formData, setFormData] = useState<SlotCreateUpdatePayload>({
    id: "",
    startTime: "",
    endTime: "",
    day: "",
    status: true,
    clinicId: "",
    date: "",
    notes: "",
  });
  const [errors, setErrors] = useState<{
    startTime?: string;
    endTime?: string;
    dateOrDay?: string;
    clinicId?: string;
    day?: string;
  }>({});

  useEffect(() => {
    if (fetchedClinicsRef.current) return;
    fetchedClinicsRef.current = true;
    const fetchClinics = async () => {
      setClinicsLoading(true);
      try {
        const res = await getClinicsByUser();
        const list = res?.data?.data || res?.data || [];
        const normalized = (Array.isArray(list) ? list : [])
          .map((c: any) => ({
            id: c.id || c._id || c.clinicId || c.value || "",
            name: c.name || c.clinicName || c.label || "",
          }))
          .filter((c) => c.id && c.name);
        setClinics(normalized);
      } catch {
        setClinics([]);
      } finally {
        setClinicsLoading(false);
      }
    };
    fetchClinics();
  }, []);

  useEffect(() => {
    if (!id || fetchedRef.current) return;
    fetchedRef.current = true;
    const fetchSlot = async () => {
      setLoading(true);
      try {
        const res = await getSlotById(id as string);
        const data = res?.data?.data || res?.data || {};
        const normalizedDay =
          data.day || (data.date ? getDayFromDate(data.date) : "");
        setFormData({
          id: data.id || data._id || (id as string),
          startTime: data.startTime || "",
          endTime: data.endTime || "",
          day: normalizedDay,
          status: data.status ?? true,
          clinicId: data.clinicId || "",
          date: data.date || "",
          notes: data.notes || "",
        });
      } catch (err: any) {
        toast.error(err?.response?.data?.message || "Failed to fetch slot");
      } finally {
        setLoading(false);
      }
    };
    fetchSlot();
  }, [id]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target as HTMLInputElement;
    if (name === "date") {
      const derivedDay = getDayFromDate(value);
      setFormData((prev) => ({
        ...prev,
        date: value,
        day: derivedDay || "",
      }));
      setErrors((prev) => ({
        ...prev,
        dateOrDay: value ? undefined : prev.dateOrDay,
        day: derivedDay ? undefined : prev.day,
      }));
      return;
    }

    if (name === "day") {
      setErrors((prev) => ({ ...prev, day: value ? undefined : prev.day }));
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "startTime" && errors.startTime) {
      setErrors((prev) => ({ ...prev, startTime: undefined }));
    }
    if (name === "endTime" && errors.endTime) {
      setErrors((prev) => ({ ...prev, endTime: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: typeof errors = {};
    if (!formData.startTime) nextErrors.startTime = "Start time is required";
    if (!formData.endTime) nextErrors.endTime = "End time is required";
    if (!formData.date && !formData.day)
      nextErrors.dateOrDay = "Either date or day is required";
    if (!formData.clinicId) nextErrors.clinicId = "Clinic is required";
    if (!formData.day) nextErrors.day = "Day is required";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    setLoading(true);
    try {
      await createOrUpdateSlot(formData);
      toast.success(
        id ? "Slot updated successfully" : "Slot created successfully"
      );
      navigate("/slots");
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to save slot");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />
      <div className="flex-1 flex flex-col">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
        <main className="p-4 md:p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full max-w-6xl mx-auto"
          >
            <motion.div
              initial={{ scale: 0.98, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="w-full rounded-3xl shadow-2xl bg-white p-8"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                  {id ? "Edit Slot" : "Create Slot"}
                </h2>
                <button
                  className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                  onClick={() => window.history.back()}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="w-4 h-4"
                    aria-hidden="true"
                  >
                    <path d="M10 19l-7-7 7-7" />
                    <path d="M3 12h18" />
                  </svg>
                  Back
                </button>
              </div>

              <form
                noValidate
                onSubmit={handleSubmit}
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    required
                    className={`mt-1 w-full p-2 border rounded-xl ${
                      errors.startTime
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                    }`}
                  >
                    <option value="">Select Start Time</option>
                    {TIME_OPTIONS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  {errors.startTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.startTime}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1">
                    End Time <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    required
                    className={`mt-1 w-full p-2 border rounded-xl ${
                      errors.endTime
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                    }`}
                  >
                    <option value="">Select End Time</option>
                    {TIME_OPTIONS.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                  {errors.endTime && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.endTime}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Date
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date || ""}
                    onChange={handleChange}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.dateOrDay
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                  />
                  {errors.dateOrDay && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.dateOrDay}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Day <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="day"
                    value={formData.day || ""}
                    onChange={handleChange}
                    disabled={Boolean(formData.date)}
                    className={`mt-1 w-full p-2 border rounded-xl ${
                      errors.day
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                    } ${formData.date ? "bg-gray-100 cursor-not-allowed" : ""}`}
                  >
                    <option value="" disabled>
                      Select Day
                    </option>
                    {days.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                  {formData.date && (
                    <p className="text-xs text-gray-500 mt-1">
                      Day auto-selected from the chosen date.
                    </p>
                  )}
                  {errors.day && (
                    <p className="text-red-500 text-sm mt-1">{errors.day}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Clinic <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="clinicId"
                    value={formData.clinicId}
                    onChange={handleChange}
                    required
                    disabled={clinicsLoading}
                    className={`mt-1 w-full p-2 border rounded-xl ${errors.clinicId
                        ? "border-red-500 focus:ring-red-500 focus:border-red-500"
                        : ""
                      }`}
                  >
                    {clinicsLoading ? (
                      <option value="">Loading clinics...</option>
                    ) : (
                      <>
                        <option value="" disabled>
                          Select Clinic
                        </option>
                        {clinics.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                  {errors.clinicId && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.clinicId}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    value={formData.notes || ""}
                    onChange={handleChange}
                    rows={3}
                    className="mt-1 w-full p-2 border rounded-xl resize-none"
                    placeholder="Optional notes"
                  />
                </div>

                <div className="md:col-span-2 flex justify-end gap-3 mt-4">
                  <button
                    type="button"
                    className="px-5 py-2 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                    onClick={() => window.history.back()}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 rounded-xl   text-white  transition disabled:opacity-60"
                  >
                    {loading ? "Saving..." : id ? "Update Slot" : "Save Slot"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default SlotsForm;
